// R115 maint — the 📥 bucket of UNSCHEDULED work orders (Jesse A2: "a bucket of
// UNSCHEDULED WOs to drag-and-drop onto dates (or click → pick a date)").
// Two surfaces over the 'wo_unscheduled' layer:
//   rail provider 'unscheduled_wos' — one row per WO in the core's 📥 To-do rail
//     (searchable; a click opens the WO inspector), led by an "All unscheduled"
//     row that opens …
//   inspector 'wo_bucket' — the full worklist in the panel: search (the
//     Maintenance board's own woSearch), group by home, and per row Book…
//     (touch-safe 21-day strip), Book on a date… (native date input, any day
//     from today), Open, and a desktop drag source. ref.id 'all' = the whole
//     portfolio; ref.id <listing_id> = one home (its 🔧 row badge opens it),
//     which also lists the home's booked WOs in view with Move….
import { registerInspector, registerSection, registerAction, registerRail, type CalendarCtx, type RailItem } from '../registry';
import type { WoLayer, UnschedWo } from '../../../../../lib/woCalendar.mjs';
import { groupByListing, searchableWo } from '../../../../../lib/woCalendar.mjs';
import { search as woSearch } from '../../../../../lib/woSearch.mjs';
import { kebabHtml, bindKebabs } from '../../../shared/components/Kebab';
import { WoQuickView } from '../../../shared/components/WoQuickView';
import { api, LAYER_ID, OFFICE, ALL_VIEWS, woLayer, PRIO_LABEL, wmd, md, ageText } from './shared';
import { pickAndBook, guardGuestNight, type BookTarget } from './book';
import { openNewWoSheet } from './newWo';

interface BucketData { layer: WoLayer | null; error: string }
const SHOW = 60;
const fine = () => { try { return window.matchMedia('(pointer: fine)').matches; } catch { return false; } };
const st = { q: '', group: false, shown: SHOW, dragging: false };
try { st.group = sessionStorage.getItem('stc_wo_group') === '1'; } catch { /* private mode */ }
export const bucketBusy = (): boolean => st.dragging;

const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const tgt = (it: UnschedWo): BookTarget => ({ id: it.id, listing_id: it.listing_id, title: it.title, prev: it.was_booked || null, guest_ok: it.guest_ok });
const prioTone = (p: string) => (p === 'emergency' ? 'bad' : p === 'urgent' ? 'warn' : p === 'pm' ? 'muted' : 'info');
/** "Home · who · due · age" — the one sub-line the rail and the bucket share. */
export function subLine(it: UnschedWo, withHome = true, sayNobody = true): string {
  return [
    withHome ? it.listing_name : '',
    it.was_booked ? `was booked ${md(it.was_booked)} — rebook` : '',   // R115 review: a missed booking
    it.who ? it.who : (sayNobody ? 'nobody on it' : ''),
    it.due_date ? (it.overdue ? `overdue · was due ${md(it.due_date)}` : `${it.due_source === 'checkout' ? 'due at checkout' : 'due'} ${md(it.due_date)}`) : '',
    ageText(it.age_days),
  ].filter(Boolean).join(' · ');
}
const searchText = (it: UnschedWo) => [it.title, it.listing_name, it.who || 'unassigned', it.priority, it.role, it.source || '', it.created_by || '', it.overdue ? 'overdue' : '', it.unassigned ? 'unassigned' : '', it.kind === 'pm' ? 'pm preventive' : '', it.was_booked ? 'missed was booked rebook' : ''].join(' ');

// ── rail provider ───────────────────────────────────────────────────────────
registerRail({
  key: 'unscheduled_wos', label: 'Work orders to schedule', layer: LAYER_ID,
  items: (ctx) => {
    const l = woLayer(ctx);
    if (!l || !l.unscheduled.length) return [];
    const head: RailItem = {
      key: 'wo_bucket:all', entity: { kind: 'wo_bucket', id: 'all' }, listing_id: null, tone: 'accent', meta: true,   // opens the list — not counted as a to-do
      title: `All ${l.unscheduled.length} unscheduled — group by home, book`, sub: 'Search, group by home, book a day or drag onto the calendar',
      search: 'all unscheduled work orders bucket group book', date: null,
    };
    return [head, ...l.unscheduled.map((it): RailItem => ({
      key: `wo:${it.id}`, entity: { kind: 'wo', id: it.id, listing_id: it.listing_id }, listing_id: it.listing_id,
      title: `${it.priority === 'emergency' ? '🚨 ' : it.priority === 'urgent' ? '⚡ ' : ''}${it.title}`, sub: subLine(it),
      tone: it.priority === 'emergency' ? 'bad' : (it.priority === 'urgent' || it.overdue || it.was_booked) ? 'warn' : 'info',
      date: null, search: searchText(it),
    }))];
  },
});

// ── the bucket inspector ────────────────────────────────────────────────────
async function loadLayer(ctx: CalendarCtx, signal: AbortSignal): Promise<BucketData> {
  const have = woLayer(ctx);
  if (have) return { layer: have, error: '' };
  // the view didn't load the layer (e.g. a 🔧 badge opened from Reservations) — ask for it directly
  const r = await api('GET', `/api/stays/calendar/layers?l=${LAYER_ID}&from=${encodeURIComponent(ctx.from)}&to=${encodeURIComponent(ctx.to)}`, undefined, signal);
  const l = r.ok && r.json && r.json.layers ? r.json.layers[LAYER_ID] : null;
  if (l && l.unavailable) return { layer: null, error: l.why || "Work orders couldn't be read — try again in a moment." };
  if (!l) return { layer: null, error: (r.json && r.json.errors && r.json.errors[LAYER_ID]) || r.error || "Couldn't load the work orders." };
  return { layer: l as WoLayer, error: '' };
}
registerInspector({
  kind: 'wo_bucket',
  title: (ref, _data, ctx) => { if (ref.id === 'all') return 'Unscheduled work orders'; const p = ctx.property(String(ref.id)); return `Work orders · ${p ? p.name : ref.id}`; },
  sub: (ref, data) => {
    const d = data as BucketData | null; const l = d && d.layer;
    if (!l) return d && d.error ? d.error : '';
    if (ref.id === 'all') return `${l.counts.unscheduled} with no day booked · ${l.counts.overdue} overdue · ${l.counts.urgent} urgent`;
    const c = l.by_listing[String(ref.id)];
    return c ? `${c.open} open · ${c.unscheduled} unscheduled${c.overdue ? ` · ${c.overdue} overdue` : ''}` : 'No open work orders';
  },
  load: (_ref, signal, ctx) => loadLayer(ctx, signal),
  busy: () => st.dragging,
  reset: () => { st.shown = SHOW; },
});

function rowHtml(it: UnschedWo, withHome: boolean): string {
  // R115 integ fix (D4): the whole title + sub block is the tap target (≥ 36px on touch) — a role=button div, so the
  // draggable row still drags from it in every browser
  return `<div class="mwb-row" draggable="true" data-mwb-drag="${esc(it.id)}" data-mwb-lid="${esc(it.listing_id)}">
      <div class="mwb-main" role="button" tabindex="0" data-mwb-open="${esc(it.id)}" data-mwb-olid="${esc(it.listing_id)}" title="Open this work order">
        <div class="mwb-top"><span class="mw-pill ${prioTone(it.priority)}">${esc(PRIO_LABEL[it.priority] || it.priority)}</span><span class="mwb-title">${esc(it.title)}</span></div>
        <div class="mwb-sub">${it.unassigned ? '<span class="mw-pill warn">unassigned</span> ' : ''}${esc(subLine(it, withHome, false))}${it.guest_ok ? ' <span class="mw-pill" title="Guest permission to enter is on file">🚪 ok</span>' : ''}</div>
      </div>
      <div class="mwb-acts">
        <button type="button" class="btn btn-secondary btn-sm" data-mwb-book="${esc(it.id)}">Book…</button>
        ${kebabHtml([
          { label: 'Open details', icon: '↗', attrs: { 'data-mwb-open': it.id, 'data-mwb-olid': it.listing_id } },
          { label: 'Book on a date…', icon: '📅', attrs: { 'data-mwb-date': it.id } },
          { label: 'Open in Maintenance', icon: '🔧', attrs: { 'data-mwb-mn': it.id } },
        ], { tip: 'More for this work order' })}
      </div>
    </div>`;
}

registerSection({
  inspector: 'wo_bucket', key: 'wo_bucket_list', title: 'Work orders', order: 10, openIn: ALL_VIEWS,
  when: () => true,
  render: (ref, data, ctx) => `<div class="mw mwb" data-mwb>${bucketInner(String(ref.id), data as BucketData, ctx)}</div>`,
  bind: (root, ref, data, ctx) => {
    const host = root.querySelector<HTMLElement>('[data-mwb]') || root;
    const redraw = () => { host.innerHTML = bucketInner(String(ref.id), data as BucketData, ctx); bindBucket(host, String(ref.id), data as BucketData, ctx, redraw); bindKebabs(host); };
    bindBucket(host, String(ref.id), data as BucketData, ctx, redraw);
    bindKebabs(host);
  },
  busy: () => st.dragging,
});

function filtered(l: WoLayer, scope: string, ctx: CalendarCtx): UnschedWo[] {
  let rows = scope === 'all' ? l.unscheduled : l.unscheduled.filter(x => x.listing_id === scope);
  const q = st.q.trim();
  if (q) {
    const res = woSearch(rows.map(searchableWo), q, { today: ctx.asOf });
    const byId = new Map(rows.map(r => [r.id, r]));
    rows = res.rows.map((r: any) => byId.get(String(r.w.id))).filter(Boolean) as UnschedWo[];
  }
  return rows;
}
function bucketInner(scope: string, d: BucketData, ctx: CalendarCtx): string {
  const l = d && d.layer;
  if (!l) return `<div class="mw-err" role="alert">${esc((d && d.error) || "Couldn't load the work orders.")}</div>`;
  const all = scope === 'all';
  const rows = filtered(l, scope, ctx);
  const total = all ? l.unscheduled.length : l.unscheduled.filter(x => x.listing_id === scope).length;
  const booked = all ? [] : (ctx.data.events.work_orders || []).filter(w => w.listing_id === scope && !w.due_only);
  const list = st.group && all
    ? groupByListing(rows.slice(0, st.shown)).map(g => `<div class="mwb-group"><div class="mw-h">${esc(g.listing_name)} · ${g.items.length}</div>${g.items.map(it => rowHtml(it, false)).join('')}</div>`).join('')
    : rows.slice(0, st.shown).map(it => rowHtml(it, all)).join('');
  return `
    <div class="mwb-tools">
      <input type="search" class="mw-input mwb-q" data-mwb-q value="${esc(st.q)}" placeholder="Search — title, home, person, “urgent”, “overdue”, “unassigned”…" aria-label="Search unscheduled work orders">
      ${all ? `<button type="button" class="chip ${st.group ? 'active' : ''}" data-mwb-group aria-pressed="${st.group ? 'true' : 'false'}">Group by home</button>` : ''}
    </div>
    <div class="mw-muted mwb-count">${st.q.trim() ? `${rows.length} of ${total} match` : `${total} with no day booked`} · Book… picks a day — taken nights allowed, past days refused${fine() ? ' · or drag a row onto its home’s row' : ''}.</div>
    <div class="mwb-list">${list || `<div class="mw-muted mwb-empty">${st.q.trim() ? 'Nothing matches that search.' : 'Every open work order here has a day. 🎉'}</div>`}</div>
    ${rows.length > st.shown ? `<button type="button" class="btn btn-ghost btn-sm" data-mwb-more>Show ${Math.min(SHOW, rows.length - st.shown)} more (${rows.length - st.shown} left)</button>` : ''}
    ${booked.length ? `<div class="mw-h mwb-bookedh">Booked in view</div>${booked.map(w => `
      <div class="mwb-row">
        <div class="mwb-main" role="button" tabindex="0" data-mwb-open="${esc(w.id)}" data-mwb-olid="${esc(w.listing_id)}" title="Open this work order"><div class="mwb-top"><span class="mw-pill ${prioTone(w.priority)}">${esc(PRIO_LABEL[w.priority] || w.priority)}</span><span class="mwb-title">${esc(w.title)}</span></div>
        <div class="mwb-sub">${esc(wmd(w.date))}${w.end ? ` → ${esc(md(w.end))}` : ''} · ${esc(w.who || 'nobody on it')}</div></div>
        <div class="mwb-acts"><button type="button" class="btn btn-secondary btn-sm" data-mwb-move="${esc(w.id)}">Move…</button></div>
      </div>`).join('')}` : ''}`;
}

function bindBucket(host: HTMLElement, scope: string, d: BucketData, ctx: CalendarCtx, redraw: () => void): void {
  const l = d && d.layer;
  const byId = new Map((l ? l.unscheduled : []).map(x => [x.id, x]));
  const q = host.querySelector<HTMLInputElement>('[data-mwb-q]');
  let t: ReturnType<typeof setTimeout> | null = null;
  q?.addEventListener('input', () => {
    if (t) clearTimeout(t);
    t = setTimeout(() => { st.q = q.value; st.shown = SHOW; const pos = q.selectionStart; redraw(); const n = host.querySelector<HTMLInputElement>('[data-mwb-q]'); if (n) { n.focus(); try { n.setSelectionRange(pos, pos); } catch { /* type=search */ } } }, 160);
  });
  host.querySelector('[data-mwb-group]')?.addEventListener('click', () => { st.group = !st.group; try { sessionStorage.setItem('stc_wo_group', st.group ? '1' : '0'); } catch { /* private mode */ } redraw(); });
  host.querySelector('[data-mwb-more]')?.addEventListener('click', () => { st.shown += SHOW; redraw(); });
  host.querySelectorAll<HTMLElement>('[data-mwb-open]').forEach(el => el.addEventListener('click', () => ctx.openInspector({ kind: 'wo', id: el.dataset.mwbOpen!, listing_id: el.dataset.mwbOlid || undefined })));
  host.querySelectorAll<HTMLElement>('.mwb-main[data-mwb-open]').forEach(el => el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.click(); } }));
  host.querySelectorAll<HTMLElement>('[data-mwb-mn]').forEach(el => el.addEventListener('click', () => WoQuickView.focusInMaintenance(el.dataset.mwbMn!)));
  host.querySelectorAll<HTMLElement>('[data-mwb-book]').forEach(el => el.addEventListener('click', () => { const it = byId.get(el.dataset.mwbBook!); if (it) pickAndBook(ctx, tgt(it)); }));
  host.querySelectorAll<HTMLElement>('[data-mwb-move]').forEach(el => el.addEventListener('click', () => {
    const w = (ctx.data.events.work_orders || []).find(x => x.id === el.dataset.mwbMove);
    if (w) pickAndBook(ctx, { id: w.id, listing_id: w.listing_id, title: w.title, prev: w.due_only ? null : w.date, guest_ok: !!w.guest_ok });   // R115 review: the permission on file (the event carries it)
  }));
  host.querySelectorAll<HTMLElement>('[data-mwb-date]').forEach(el => el.addEventListener('click', () => { const it = byId.get(el.dataset.mwbDate!); if (it) bookOnDateSheet(ctx, it); }));
  host.querySelectorAll<HTMLElement>('[data-mwb-drag]').forEach(el => {
    el.addEventListener('dragstart', (e) => {
      const ev = e as DragEvent; const id = el.dataset.mwbDrag!; const lid = el.dataset.mwbLid || '';
      if (!ev.dataTransfer) return;
      ev.dataTransfer.effectAllowed = 'move';
      ev.dataTransfer.setData('text/wo', id);   // the Maintenance month calendar's drop format
      ev.dataTransfer.setData('application/x-stc-entity', JSON.stringify({ kind: 'wo', id, listing_id: lid }));
      ev.dataTransfer.setData('text/plain', `wo:${id}`);
      st.dragging = true; el.classList.add('dragging');
    });
    el.addEventListener('dragend', () => { st.dragging = false; el.classList.remove('dragging'); });
  });
}

/** Book on any day from today (beyond the 21-day strip) with the native date input. */
function bookOnDateSheet(ctx: CalendarCtx, it: UnschedWo): void {
  const sheet = ctx.openSheet(`
    <form class="mw mw-sheet" data-mwb-dateform novalidate>
      <p>Book “<b>${ctx.esc(it.title)}</b>” at ${ctx.esc(it.listing_name)} on:</p>
      <label class="mw-field">Day<input class="mw-input" type="date" name="day" min="${ctx.esc(ctx.asOf)}" value="${ctx.esc(it.due_date && it.due_date >= ctx.asOf ? it.due_date : ctx.asOf)}" required></label>
      <div class="mw-err" data-mw-err role="alert" hidden></div>
      <div class="mw-sheet-foot" data-bottom-bar><button type="button" class="btn btn-secondary" data-mw-cancel>Cancel</button><button type="submit" class="btn btn-primary">Book</button></div>
    </form>`, { title: 'Book on a date' });
  const form = sheet.querySelector<HTMLFormElement>('[data-mwb-dateform]');
  sheet.querySelector('[data-mw-cancel]')?.addEventListener('click', () => ctx.closeSheet());
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    const day = String((form.elements.namedItem('day') as HTMLInputElement | null)?.value || '');
    const err = sheet.querySelector<HTMLElement>('[data-mw-err]');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day < ctx.asOf) { if (err) { err.hidden = false; err.textContent = 'Pick a day from today on.'; } return; }
    ctx.closeSheet();
    guardGuestNight(ctx, tgt(it), day);
  });
}

// "+ New work order" for one home, from its bucket
registerAction({
  key: 'wo_bucket_new', inspector: 'wo_bucket', label: 'New work order…', icon: '＋', order: 10, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (ref) => ref.id !== 'all',
  run: (ref, _d, ctx) => openNewWoSheet(ctx, String(ref.id), ctx.asOf),
});
